import {inject, Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  PaymentPollingStatus,
  PaymentVerificationResult
} from '@features/payment/domain/model/payment.model';
import {PaymentStrategy} from '@features/payment/domain/ports/strategy/payment-strategy.port';
import {Payment} from '@features/payment/domain/entities/payment.entity';
import {PaymentCheckoutError} from '@features/payment/domain/model/payment.errors';
import {CheckoutGateway} from '../../gateways/checkout.gateway';
import {CheckoutPayloadMapper} from '../../mappers/checkout-payload.mapper';

@Injectable({providedIn: 'root'})
export class OfflineQueueStrategy implements PaymentStrategy {
  private readonly checkoutGateway = inject(CheckoutGateway);
  private readonly mapper = inject(CheckoutPayloadMapper);

  private readonly QUEUED_SIGNAL = 'queued-by-sw';
  private readonly OFFLINE_PARAMS = {'x-offline-sync': 'true'};

  checkout(payments: Payment[]): Observable<string> {
    const payload = this.mapper.toCheckoutPayload(payments);

    return this.checkoutGateway.checkoutOnline(payload, {params: this.OFFLINE_PARAMS}).pipe(
      map((id) => id || this.QUEUED_SIGNAL),
      catchError((error: { status?: number }) => this.handleOfflineError(error)),
    );
  }

  verify(): Observable<PaymentVerificationResult> {
    return of({status: PaymentPollingStatus.COMPLETED});
  }

  private handleOfflineError(error: { status?: number }): Observable<string> {
    const isQueuedByServiceWorker = error.status === 0 || error.status === 202;

    return isQueuedByServiceWorker
      ? of(this.QUEUED_SIGNAL)
      : throwError(() => new PaymentCheckoutError());
  }
}
